'use client';

import React from 'react';
import { CentralizedDrawer } from '@wtc/ui/components/global-drawer-stack';
import type { ChatSummary } from '../../../types';
import { SidebarContent } from './SidebarContent';
import type { TabType, TabDefinition } from '../types';

interface MobileDrawerProps {
  chats: ChatSummary[];
  selectedChatId: string | null;
  onSelectChat: (chatId: string) => void;
  onCreateChat: () => void;
  onRestoreChat: (chatId: string) => void;
  onDeleteChat?: (chatId: string) => void;
  onDeleteAllChats?: () => void | Promise<void>;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  tabs: TabDefinition[];
  filteredChats: ChatSummary[];
  loadTriggerRef: React.RefObject<HTMLDivElement | null>;
  hasMoreChats: boolean;
  isLoadingMoreChats: boolean;
  truncateTitle: (title: string, maxLength?: number) => string;
  formatDate: (dateString: string) => string;
  onOpenSettings?: () => void;
  onOpenCapabilities?: () => void;
  mcpConnectionsHref?: string;
  routinesHref?: string;
  onClose?: () => void;
  meridianLogoUrl?: string;
}

/**
 * Mobile Drawer Component
 * Uses CentralizedDrawer from global drawer stack
 * Encapsulates all mobile-specific sidebar logic
 */
export function MobileDrawer({
  chats,
  selectedChatId,
  onSelectChat,
  onCreateChat,
  onRestoreChat,
  onDeleteChat,
  onDeleteAllChats,
  activeTab,
  setActiveTab,
  tabs,
  filteredChats,
  loadTriggerRef,
  hasMoreChats,
  isLoadingMoreChats,
  truncateTitle,
  formatDate,
  onOpenSettings,
  onOpenCapabilities,
  mcpConnectionsHref,
  routinesHref,
  onClose,
  meridianLogoUrl = '/meridian.png',
}: MobileDrawerProps) {
  // Wrapper functions for mobile-specific behavior
  const handleSelectChatWithClose = (chatId: string) => {
    onSelectChat(chatId);
    onClose?.();
  };

  const handleChatHistoryClickWithClose = () => {
    onClose?.();
  };

  return (
    <CentralizedDrawer
      drawerId="mobile-sidebar-drawer"
      side="left"
      height="100vh"
      showCloseButton={false}
      enableSwipeGesture={true}
      className="p-0 !z-[9999]"
    >
      {/* Sidebar Content */}
      <SidebarContent
        onCreateChat={onCreateChat}
        onChatHistoryClick={handleChatHistoryClickWithClose}
        chats={chats}
        selectedChatId={selectedChatId}
        onSelectChat={handleSelectChatWithClose}
        onRestoreChat={onRestoreChat}
        onDeleteChat={onDeleteChat}
        onDeleteAllChats={onDeleteAllChats}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
        filteredChats={filteredChats}
        loadTriggerRef={loadTriggerRef}
        hasMoreChats={hasMoreChats}
        isLoadingMoreChats={isLoadingMoreChats}
        truncateTitle={truncateTitle}
        formatDate={formatDate}
        onOpenSettings={onOpenSettings}
        onOpenCapabilities={onOpenCapabilities}
        mcpConnectionsHref={mcpConnectionsHref}
        routinesHref={routinesHref}
        isCollapsed={false}
        onClose={onClose}
        meridianLogoUrl={meridianLogoUrl}
      />
    </CentralizedDrawer>
  );
}
